import { useMemo, useState } from 'react';
import { CampaignType } from '@nkr/enums/index';
import { IMicrositeCampaignListing } from '@nkr/models/microsites';
import { BodyScrollEvent, ColDef, FirstDataRenderedEvent, GridReadyEvent, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { CampaignGridWrapper } from '../../styles';
import { Utility } from '@nkr/services/utility';
import { Label } from 'react-bootstrap';
import RankRenderer from '@nkr/views/admin/center/list/common/rank-renderer';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import ActionButtonRenderer from './action-button-renderer';

interface IProps {
    data: IMicrositeCampaignListing[],
    isAdmin: boolean,
    onMenuSelection: (eventKey: number, campaignId: number) => void,
    onGetCampaignPdf: (campaignId: number) => void
}

const Grid = (props: IProps) => {
    const [menuDisplayed, setMenuDisplayed] = useState(false);

    const onGridReady = (e: GridReadyEvent) => {
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = (e: BodyScrollEvent) => {
        if (menuDisplayed) {
            document.body.click();
            setMenuDisplayed(false);
        }
    };

    const handleMenuToggle = (show: boolean) => {
        setMenuDisplayed(show);
    };

    const cols: ColDef[] = [
        {
            colId: 'Rank',
            headerName: 'Rank',
            width: 60,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Rank</SortableHeaderComponent>,
            cellRendererFramework: (props: ICellRendererParams) => <RankRenderer {...props} />,
        },
        {
            colId: 'center',
            headerName: 'Center',
            field: 'centerAlias',
            width: 160,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IMicrositeCampaignListing;

                if (data.centerId == null) {
                    return <Label>- NO CENTER -</Label>
                }

                return <>{params.value}</>
            },
        },
        {
            colId: 'campaign',
            headerName: 'Campaign',
            field: 'campaignName',
            width: 250,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Campaign</SortableHeaderComponent>,
        },
        {
            colId: 'micrositeTypeName',
            headerName: 'Microsite Type',
            field: 'micrositeTypeName',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Microsite<br />Type</SortableHeaderComponent>,
        },
        {
            colId: 'selfRegistrationKey',
            headerName: 'Key',
            field: 'selfRegistrationKey',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Key</SortableHeaderComponent>,
        },
        {
            colId: 'startDate',
            headerName: 'Start Date',
            field: 'startDate',
            comparator: Utility.dateComparator,
            width: 110,
            suppressSizeToFit: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Start Date</SortableHeaderComponent>,
        },
        {
            colId: 'endDate',
            headerName: 'End Date',
            field: 'endDate',
            comparator: Utility.dateComparator,
            width: 110,
            suppressSizeToFit: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>End Date</SortableHeaderComponent>,
        },
        {
            colId: 'campaignTypeName',
            headerName: 'Campaign Type',
            field: 'campaignTypeName',
            width: 120,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Campaign<br />Type</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let isMailedOut = params.data.campaignTypeId == CampaignType.MailedOut;

                return isMailedOut ? <>Mailed Out</> : <>In House</>;
            }
        },
        {
            colId: 'mailingCount',
            headerName: 'Mailing List Count',
            field: 'mailingCount',
            width: 90,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Mailing<br />List Count</SortableHeaderComponent>,
        },
        {
            colId: 'starterSitesCreated',
            headerName: 'Starter Sites Created',
            field: 'starterSitesCreated',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Starter Sites<br />Created</SortableHeaderComponent>,
            comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                let num1: number = parseInt(valueA, 10);
                let num2: number = parseInt(valueB, 10);
                return num1 - num2;
            },
        },
        {
            colId: 'customSitesCreated',
            headerName: 'Custom Sites Created',
            field: 'customSitesCreated',
            width: 100,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Custom Sites<br />Created</SortableHeaderComponent>,
            comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
                let num1: number = parseInt(valueA, 10);
                let num2: number = parseInt(valueB, 10);
                return num1 - num2;
            },
        },
        {
            colId: 'links',
            headerName: 'Links',
            width: 250,
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Links</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) =>
                <>
                    <a href={(__NKR_ORG_URL__ + '/register/' + params.data.selfRegistrationKey)} target='_blank'>Self Registration</a>
                    <span style={{ marginLeft: 2, marginRight: 2 }}>/</span>
                    <a onClick={() => props.onGetCampaignPdf(params.data.id)}>PDF Form</a>
                </>
        },
        {
            colId: 'Actions',
            cellClass: 'action-menu',
            headerClass: 'campaign-list-header',
            width: 80,
            suppressSizeToFit: true,
            cellRendererFramework: (params: ICellRendererParams) =>
                <ActionButtonRenderer {...params}
                    isAdmin={props.isAdmin}
                    onMenuToggle={handleMenuToggle}
                    onMenuSelection={props.onMenuSelection}
                />
        }
    ];

    const columnDefs = useMemo(() => cols, []);

    const data = useMemo(() => props.data, [props.data]);

    return (
        <>
            <CampaignGridWrapper className='ag-theme-bootstrap'>
                <AgGridReact
                    defaultColDef={{
                        cellClass: 'campaign-list-cell',
                        headerClass: 'campaign-list-header',
                        sortable: true,
                    }}
                    columnDefs={columnDefs}
                    rowData={data}
                    headerHeight={45}
                    rowHeight={30}
                    noRowsOverlayComponentFramework={() => <div>No campaigns found...</div>}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onFirstDataRendered}
                    onBodyScroll={onBodyScroll}
                />
                <div style={{ marginTop: 5 }}>{data.length} campaigns...</div>
            </CampaignGridWrapper>
        </>
    )
}

export default Grid;